import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTheme, Headline, Portal, Modal, ActivityIndicator } from "react-native-paper";
import { useSelector, useDispatch } from "react-redux";
import { View, StyleSheet, Image, ScrollView } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import { Screen, Text } from "../../components";
import { Stack } from "../../components/layout/stack";
import { ServiceRequestTable } from "../../components/service-request-table";
import { useNavigation, useFocusEffect } from "@react-navigation/native";

import { translate } from "../../i18n/translate";
import { ThemeType } from "../../theme/theme";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";

import { setPaymentCancelled } from "../../redux/actions/actionCreators";
import { ReservationService } from "../../api/ReservationService";
import { getPropertyAddressFromPropertyProfile } from "../../utils";
import { RootState } from "../../redux/reducers";
import moment from "moment";
import { fontSize } from "../../theme/fontSize";
import { AccountSystemTypes } from "../../enums";
import { Grid, Container, Row, Column } from "ui-grid";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { BottomSheetModal } from "../../components/new/bottom-sheet-modal/bottom-sheet-modal";
import { Button } from "ui-components";

export const ServiceRequestSpecificReservation = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const theme = useTheme() as ThemeType;
  const dispatch = useDispatch();
  const styles = getStyles(theme);
  const { params } = route;
  const logger = useSelector((state: RootState) => state.logger);
  const { currentPropertyProfile, propName, reservationId } = params;
  const [networkError, setNetworkError] = useState<any>(null);
  const [networkErrorTx, setNetworkErrorTx] = useState<any>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [serviceRequests, setServiceRequests] = useState<any[]>([]);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState<any>();
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);
  const reservationService = new ReservationService(cognitoUser, dispatch);
  const propAddress = getPropertyAddressFromPropertyProfile(currentPropertyProfile);
  const isYardi = currentPropertyProfile.account_system_type === AccountSystemTypes.YARDI;
  const sheetRef = useRef<any>();

  const layoutEffect = useCallback(() => {
    setLoading(true);
    (async () => {
      try {
        let serviceRequests = await reservationService.getServiceRequests(reservationId);
        if (!Array.isArray(serviceRequests))
          throw new Error("Something went wrong: " + serviceRequests?.message);
        serviceRequests = serviceRequests.map((request: any) => ({
          ...request,
          category_description: request.category_description?.replace("&amp;", "&"),
        }));
        serviceRequests = serviceRequests.sort(
          (a: any, b: any) => moment(a.reported_date) < moment(b.reported_date)
        );
        setServiceRequests(serviceRequests);
      } catch (error) {
        logger.error(error);
        setNetworkErrorTx("serviceStack.failureGetRequests");
      }
      setLoading(false);
    })();
  }, [reservationId]);
  useFocusEffect(layoutEffect);

  return (
    <Screen style={styles.container} preset="scroll" unsafe={true} dontUseAutoPadding>
      <Grid>
        <Container>
          <Row>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow, styles.header]}>
              <View style={styles.inputRowInner}>
                <Stack space="small" style={styles.spaceBetweenColumn}>
                  <ServiceRequestDetailsModal
                    serviceRequest={selectedServiceRequest}
                    styles={styles}
                    onCloseHandler={() => setSelectedServiceRequest(null)}
                    sheetRef={sheetRef}
                  />
                  <View style={styles.propertyContainer}>
                    <Text style={[styles.text, { fontWeight: "bold" }]}>{propName}</Text>
                    <Text style={styles.text}>{propAddress}</Text>
                  </View>
                  <Stack space="smaller">
                    <Headline style={{ fontWeight: "bold" }}>
                      {translate("homeStack.serviceRequestHistory")}
                    </Headline>
                    <ServiceRequestTable
                      isLoading={isLoading}
                      serviceRequests={serviceRequests}
                      networkError={networkError}
                      networkErrorTx={networkErrorTx}
                      onRowPressHandler={setSelectedServiceRequest}
                    />
                  </Stack>
                </Stack>
                <View style={{ marginBottom: 16 }} />
              </View>
            </Column>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
          </Row>
        </Container>
      </Grid>
      <BottomNav backgroundColor={theme.colors.white}></BottomNav>
      <Footer />
    </Screen>
  );
};

interface ServiceRequestDetailsModalParams {
  serviceRequest: any;
  styles: any;
  onCloseHandler: Function;
  sheetRef?: any;
}

const ServiceRequestDetailsModal = ({
  serviceRequest,
  onCloseHandler,
  styles,
  sheetRef,
}: ServiceRequestDetailsModalParams) => {
  if (!serviceRequest) return null;
  const description = serviceRequest?.description?.replace(/&#xA;/g, "\n").replace(/&#xD;/g, " ");

  useEffect(() => {
    if (serviceRequest) sheetRef?.current?.open();
    else sheetRef?.current?.close();
  }, [serviceRequest]);

  return (
    <BottomSheetModal ref={sheetRef} onDismiss={onCloseHandler} height={350}>
      <View style={{ padding: 6, flex: 1 }}>
        <Stack style={styles.modalContent}>
          <Text tx="serviceStack.serviceRequestDetails" style={styles.modalTitle} />
          <Stack space="tiny">
            <Text tx="serviceStack.serviceRequestCategory" style={styles.boldText} />
            <Text>{serviceRequest?.category_description}</Text>
          </Stack>
          <Stack space="tiny">
            <Text tx="serviceStack.descriptionOfIssue" style={styles.boldText} />
            <ScrollView style={{ maxHeight: 250 }}>
              <Text>{description}</Text>
            </ScrollView>
          </Stack>
        </Stack>
        <View style={{ flex: 1, flexDirection: "row" }} />
        <Button
          text={translate("homeStack.close") as string}
          onPress={onCloseHandler}
          mode="contained"
          style={styles.modalButton}
        />
      </View>
    </BottomSheetModal>
  );
};

const getStyles = (theme: ThemeType) => {
  const text = { color: theme.colors.surface };
  const { colors, spacing } = theme;
  return StyleSheet.create({
    container: {
      backgroundColor: colors.background,
      paddingTop: spacing.large,
      zIndex: -1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: theme.colors.white,
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: theme.colors.white,
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: theme.colors.white,
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    propertyContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: spacing.medium,
      paddingVertical: spacing.large,
      marginBottom: spacing.smaller,
      borderWidth: 1,
      borderColor: colors.white,
      backgroundColor: colors.white,
      zIndex: 2,
      elevation: 3,
      shadowColor: theme.colors.darkBg,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.24,
      shadowRadius: 3,
    },
    modalContainer: {
      display: "flex",
      padding: spacing.medium,
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
    },
    modalContent: {
      padding: spacing.smaller,
      paddingBottom: spacing.medium,
    },
    modalTitle: {
      fontSize: fontSize.large,
      fontWeight: "bold",
      marginVertical: spacing.tiny,
    },
    modalButton: {
      width: "100%",
      alignSelf: "center",
    },
    text: {
      fontSize: fontSize.medium,
    },
    boldText: {
      fontSize: fontSize.medium,
      fontWeight: "bold",
    },
    spaceBetweenColumn: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      }),
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    inputRowInner: {
      justifyContent: "center",
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        iosPhone: { paddingVertical: 3 },
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: { paddingVertical: 3 },
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
  });
};
